/**
 * MainMenu.tsx — WEB.4.MM (P0.3)
 *
 * Cold-start splash. Shown when `gamePhase === "menu"` (the default for a
 * fresh page-load). Click "Start Game" → `gamePhase = "char-select"` → CharSelect stub → "playing".
 *
 * Future expansion (P0.3 polish round / P1.5 i18n):
 *   - Seat-count picker (2 / 3 / 4)
 *   - Player name inputs
 *   - Language picker (en / th)
 *   - Seed picker / "Continue Last Game"
 *
 * For MVP: a single Start Game button (hot-seat 4-player, default seed).
 *
 * VISIBLE WHEN: store.gamePhase === "menu".
 */

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { crossfadeBGM, stopBGM } from "../../audio/audio.js";
import { useI18n } from "../../i18n/useI18n.js";
import { useGameStore } from "../../store/game-store.js";
import { GAME_VERSION } from "../../version.js";
import { Tutorial, getTutorialShown } from "../Tutorial/Tutorial.js";
import styles from "./MainMenu.module.css";

// Typed shim for the non-standard beforeinstallprompt event.
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function MainMenu(): React.ReactElement | null {
  const gamePhase = useGameStore((s) => s.gamePhase);
  const startGame = useGameStore((s) => s.startGame);
  const lang = useGameStore((s) => s.lang);
  const setLang = useGameStore((s) => s.setLang);
  const { t } = useI18n();
  const audioMuted = useGameStore((s) => s.audioMuted);

  // Tutorial: show on first load if not yet seen.
  // Initialized once from localStorage so the flag is stable across re-renders.
  const [tutorialVisible, setTutorialVisible] = useState(() => !getTutorialShown());

  // PWA install prompt — capture the deferred event; null once installed or dismissed.
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  // Listen for the beforeinstallprompt event (fires only when the app is NOT
  // already installed and the browser considers it installable).
  useEffect(() => {
    function onBeforeInstall(e: Event) {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    // If already running as installed PWA, appinstalled fires — clear prompt.
    function onInstalled() {
      setInstallPrompt(null);
    }
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") setInstallPrompt(null);
  };

  const startBtnRef = useRef<HTMLButtonElement | null>(null);

  // BGM: play main menu track when menu is visible and not muted.
  useEffect(() => {
    if (gamePhase !== "menu" || audioMuted) return;
    crossfadeBGM("menu", 800);
    return () => {
      stopBGM(600);
    };
  }, [gamePhase, audioMuted]);

  // Focus Start button when menu opens (a11y parity with SeatHandoff +
  // PauseMenu — keyboard / AT users land on the actionable element).
  useEffect(() => {
    if (gamePhase !== "menu") return;
    const id = window.setTimeout(() => {
      startBtnRef.current?.focus();
    }, 0);
    return () => {
      window.clearTimeout(id);
    };
  }, [gamePhase]);

  // Enter to start (matches the focus state; Space also activates the focused
  // button natively via the browser).
  useEffect(() => {
    if (gamePhase !== "menu") return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Enter") {
        // Only fire if the Start button (or nothing) is focused — don't steal
        // Enter from an arbitrary text input or future menu control.
        const active = document.activeElement;
        if (active === null || active === document.body || active === startBtnRef.current) {
          e.preventDefault();
          startGame();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [gamePhase, startGame]);

  if (gamePhase !== "menu") return null;

  return (
    <>
      {tutorialVisible && <Tutorial onDismiss={() => setTutorialVisible(false)} />}
      <dialog
        open
        className={styles.overlay}
        data-testid="main-menu-overlay"
        aria-labelledby="main-menu-title-text"
      >
        <div className={styles.panel}>
          <div className={styles.eyebrow} aria-hidden="true">
            {t("mainMenu.eyebrow")}
          </div>
          <h1 id="main-menu-title-text" className={styles.title} data-testid="main-menu-title">
            {t("mainMenu.title")}
          </h1>
          <p className={styles.subtitle}>{t("mainMenu.subtitle")}</p>

          <div className={styles.buttonStack}>
            <button
              ref={startBtnRef}
              type="button"
              className={styles.btnPrimary}
              onClick={() => startGame()}
              data-testid="main-menu-start"
            >
              {t("mainMenu.startGame")}
            </button>
            <button
              type="button"
              className={styles.btn}
              onClick={() => setTutorialVisible(true)}
              data-testid="main-menu-how-to-play"
            >
              {t("mainMenu.howToPlay")}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnDisabled}`}
              disabled
              data-testid="main-menu-continue"
              title={t("mainMenu.continueTitle")}
            >
              {t("mainMenu.continueSoon")}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnDisabled}`}
              disabled
              data-testid="main-menu-settings"
              title={t("mainMenu.settingsTitle")}
            >
              {t("mainMenu.settingsSoon")}
            </button>
            <button
              type="button"
              className={styles.btn}
              onClick={() => {
                window.open("https://wattsandwealth.online/feedback", "_blank", "noopener");
              }}
              data-testid="main-menu-feedback"
              title={t("mainMenu.feedbackTitle")}
            >
              {t("mainMenu.feedback")}
            </button>
          </div>

          {/* P1.5 language switcher — minimal toggle row. */}
          <div className={styles.langRow} data-testid="main-menu-lang-row">
            <button
              type="button"
              className={`${styles.langBtn}${lang === "en" ? ` ${styles.langBtnActive}` : ""}`}
              onClick={() => setLang("en")}
              data-testid="main-menu-lang-en"
              aria-pressed={lang === "en"}
            >
              {t("mainMenu.languageEn")}
            </button>
            {/* Thai not localized yet — disabled "coming soon" until the th
                translation pass ships. Keep the slot visible so players know
                it's planned. */}
            <button
              type="button"
              className={styles.langBtn}
              disabled
              aria-disabled="true"
              data-testid="main-menu-lang-th"
              title="Thai language — coming soon"
              style={{ opacity: 0.4, cursor: "not-allowed" }}
            >
              {t("mainMenu.languageTh")} · soon
            </button>
          </div>

          <div className={styles.hint} aria-hidden="true">
            {t("mainMenu.pressEnter")}
          </div>

          {/* PWA install prompt — only shown when browser fires beforeinstallprompt
              (i.e. app is not yet installed and meets PWA installability criteria). */}
          {installPrompt !== null && (
            <div className={styles.installRow}>
              <button
                type="button"
                className={styles.installBtn}
                onClick={handleInstall}
                data-testid="main-menu-install-pwa"
              >
                {t("mainMenu.installApp")}
              </button>
            </div>
          )}
        </div>

        {/* Version chip — bottom-right corner. Tiny, dim, non-interactive.
            Lets facilitators/devs identify the deployed build at a glance. */}
        <div
          className={styles.versionLabel}
          data-testid="main-menu-version"
          aria-hidden="true"
        >
          v{GAME_VERSION}
        </div>
      </dialog>
    </>
  );
}
